import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { CorrelationIdService } from './correlation-id.service';

/**
 * Log level enum matching backend API
 */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/**
 * Error info structure for exceptions
 */
export interface ErrorInfo {
  message: string;
  stack?: string;
  name?: string;
}

/**
 * UI Log payload structure matching backend API schema
 */
export interface UILogPayload {
  level: LogLevel;
  message: string;
  error?: ErrorInfo;
  context?: Record<string, unknown>;
  timestamp?: string;
  correlationId?: string;
  url?: string;
  userAgent?: string;
  sessionId?: string;
  userId?: string;
}

/**
 * Batch logs payload structure
 */
export interface BatchLogsPayload {
  logs: UILogPayload[];
}

/**
 * Configuration for the telemetry logger
 */
export interface TelemetryLoggerConfig {
  enabled: boolean;
  endpoint: string;
  batchEndpoint: string;
  fallbackToConsole: boolean;
  batchSize: number;
  batchIntervalMs: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: TelemetryLoggerConfig = {
  enabled: true,
  endpoint: '/v1/telemetry/log',
  batchEndpoint: '/v1/telemetry/batch',
  fallbackToConsole: true,
  batchSize: 10,
  batchIntervalMs: 5000,
};

/**
 * TelemetryLoggerService
 * 
 * Angular service for sending logs to the backend telemetry endpoints.
 * Supports correlation ID tracking, batch logging, and automatic error capture.
 * 
 * Usage:
 * ```typescript
 * class MyComponent {
 *   private readonly logger: ScopedLogger;
 *
 *   constructor(private readonly telemetryLogger: TelemetryLoggerService) {
 *     // Initialize inside the constructor — class field initializers run
 *     // before parameter properties are assigned.
 *     this.logger = this.telemetryLogger.forContext('MyComponent');
 *   }
 *
 *   doSomething() {
 *     this.logger.info('Action performed', { actionId: '123' });
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TelemetryLoggerService implements OnDestroy {
  private config: TelemetryLoggerConfig;
  private logBuffer: UILogPayload[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isBrowser: boolean;

  constructor(
    private readonly http: HttpClient,
    private readonly correlationIdService: CorrelationIdService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.config = { ...DEFAULT_CONFIG };
    this.initBatchTimer();
  }

  /**
   * Configure the telemetry logger
   */
  configure(config: Partial<TelemetryLoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.initBatchTimer();
  }

  /**
   * Get the API base URL from environment
   */
  private getBaseUrl(): string {
    return environment.API_BASE_URL || '';
  }

  /**
   * Initialize or reset the batch timer
   */
  private initBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    if (this.config.batchIntervalMs > 0) {
      this.batchTimer = setInterval(() => {
        this.flushBatch();
      }, this.config.batchIntervalMs);
    }
  }

  /**
   * Create a scoped logger with a specific context name
   */
  forContext(contextName: string, metadata?: Record<string, unknown>): ScopedLogger {
    return new ScopedLogger(this, contextName, metadata);
  }

  /**
   * Log an error message
   */
  error(message: string, error?: unknown, metadata?: Record<string, unknown>): void {
    this.log('error', message, this.extractErrorInfo(error), metadata);
  }

  /**
   * Log a warning message
   */
  warn(message: string, metadata?: Record<string, unknown>): void {
    this.log('warn', message, undefined, metadata);
  }

  /**
   * Log an info message
   */
  info(message: string, metadata?: Record<string, unknown>): void {
    this.log('info', message, undefined, metadata);
  }

  /**
   * Log a debug message
   */
  debug(message: string, metadata?: Record<string, unknown>): void {
    this.log('debug', message, undefined, metadata);
  }

  /**
   * Track an exception with full stack trace
   */
  trackException(error: unknown, context?: Record<string, unknown>): void {
    const errorInfo = this.extractErrorInfo(error);
    this.log('error', errorInfo?.message || 'Unknown error', errorInfo, {
      ...context,
      isException: true,
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    this.log('info', `[Event] ${eventName}`, undefined, {
      ...properties,
      eventName,
      isEvent: true,
    });
  }

  /**
   * Internal log method
   */
  private log(
    level: LogLevel,
    message: string,
    error?: ErrorInfo,
    metadata?: Record<string, unknown>
  ): void {
    const payload = this.buildPayload(level, message, error, metadata);

    // Always log to console in development or if fallback is enabled
    if (this.config.fallbackToConsole || !environment.production) {
      this.logToConsole(level, message, error, metadata);
    }

    if (!this.config.enabled) {
      return;
    }

    // Add to batch buffer
    this.logBuffer.push(payload);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.config.batchSize) {
      this.flushBatch();
    }
  }

  /**
   * Build the log payload
   */
  private buildPayload(
    level: LogLevel,
    message: string,
    error?: ErrorInfo,
    metadata?: Record<string, unknown>
  ): UILogPayload {
    const correlationId = this.correlationIdService.getCorrelationId();
    
    const payload: UILogPayload = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: this.redactSensitiveData(metadata),
    };

    if (error) {
      payload.error = error;
    }

    if (correlationId) {
      payload.correlationId = correlationId;
    }

    if (this.isBrowser) {
      payload.url = globalThis.location.href;
      payload.userAgent = globalThis.navigator.userAgent;

      // Add session and user info if available
      // sessionId lives in sessionStorage; userId is persisted in localStorage by user.service.ts
      const sessionId = sessionStorage.getItem('sessionId');
      const userId = localStorage.getItem('userId');

      if (sessionId) {
        payload.sessionId = sessionId;
      }
      if (userId) {
        payload.userId = userId;
      }
    }

    return payload;
  }

  /**
   * Extract error information from an Error or unknown type
   */
  private extractErrorInfo(error: unknown): ErrorInfo | undefined {
    if (!error) {
      return undefined;
    }

    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    }

    if (typeof error === 'string') {
      return {
        message: error,
      };
    }

    if (typeof error === 'object' && error !== null) {
      const errorObj = error as Record<string, unknown>;
      return {
        message: this.coerceToString(errorObj['message'] ?? errorObj['error'], 'Unknown error'),
        stack: this.coerceToString(errorObj['stack'], ''),
        name: this.coerceToString(errorObj['name'], 'Error'),
      };
    }

    return {
      message: this.coerceToString(error, 'Unknown error'),
    };
  }

  /**
   * Safely coerce an unknown value to a string, avoiding [object Object] output
   */
  private coerceToString(value: unknown, fallback: string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return fallback;
  }

  /**
   * Redact sensitive data from logs
   */
  private redactSensitiveData(data?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!data) {
      return undefined;
    }

    const redactPatterns = [
      /password/i,
      /secret/i,
      /token/i,
      /apikey/i,
      /api_key/i,
      /authorization/i,
      /credential/i,
      /private/i,
      /key$/i,
      /^auth/i,
      /session/i,
      /cookie/i,
    ];

    const redact = (obj: Record<string, unknown>): Record<string, unknown> => {
      const result: Record<string, unknown> = {};
      
      for (const [key, value] of Object.entries(obj)) {
        const shouldRedact = redactPatterns.some(pattern => pattern.test(key));
        
        if (shouldRedact) {
          result[key] = '[REDACTED]';
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          result[key] = redact(value as Record<string, unknown>);
        } else {
          result[key] = value;
        }
      }
      
      return result;
    };

    return redact(data);
  }

  /**
   * Log to browser console
   */
  private logToConsole(
    level: LogLevel,
    message: string,
    error?: ErrorInfo,
    metadata?: Record<string, unknown>
  ): void {
    const correlationId = this.correlationIdService.getCorrelationId();
    const prefix = correlationId ? `[${correlationId.substring(0, 8)}]` : '';
    const fullMessage = `${prefix} ${message}`;

    const logData = {
      ...metadata,
      ...(error ? { error } : {}),
      correlationId,
    };

    switch (level) {
      case 'error':
        console.error(fullMessage, logData);
        break;
      case 'warn':
        console.warn(fullMessage, logData);
        break;
      case 'info':
        console.info(fullMessage, logData);
        break;
      case 'debug':
        console.debug(fullMessage, logData);
        break;
    }
  }

  /**
   * Flush the log buffer to the server
   */
  flushBatch(): void {
    if (this.logBuffer.length === 0) {
      return;
    }

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    const url = `${this.getBaseUrl()}${this.config.batchEndpoint}`;
    const correlationId = this.correlationIdService.getCorrelationId();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (correlationId) {
      headers = headers.set('X-Correlation-ID', correlationId);
    }

    const payload: BatchLogsPayload = { logs: logsToSend };

    this.http.post(url, payload, { headers }).subscribe({
      error: (err) => {
        // On error, log to console and optionally retry
        if (this.config.fallbackToConsole) {
          console.error('[TelemetryLogger] Failed to send batch logs:', err);
          logsToSend.forEach(log => {
            console.log('[TelemetryLogger] Unbatched log:', log);
          });
        }
      },
    });
  }

  /**
   * Send a single log immediately (bypassing batch)
   */
  sendImmediate(payload: UILogPayload): void {
    if (!this.config.enabled) {
      return;
    }

    const url = `${this.getBaseUrl()}${this.config.endpoint}`;
    const correlationId = this.correlationIdService.getCorrelationId();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (correlationId) {
      headers = headers.set('X-Correlation-ID', correlationId);
    }

    this.http.post(url, payload, { headers }).subscribe({
      error: (err) => {
        if (this.config.fallbackToConsole) {
          console.error('[TelemetryLogger] Failed to send log:', err);
        }
      },
    });
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }
    // Flush remaining logs
    this.flushBatch();
  }
}

/**
 * Scoped Logger
 * 
 * A logger instance with a specific context name (e.g., class name, module name)
 * that is automatically included in all log messages.
 */
export class ScopedLogger {
  private readonly contextName: string;
  private readonly metadata: Record<string, unknown>;

  constructor(
    private readonly telemetryService: TelemetryLoggerService,
    contextName: string,
    metadata?: Record<string, unknown>
  ) {
    this.contextName = contextName;
    this.metadata = metadata || {};
  }

  /**
   * Create a child scoped logger
   */
  forContext(childContext: string, metadata?: Record<string, unknown>): ScopedLogger {
    return new ScopedLogger(
      this.telemetryService,
      `${this.contextName}.${childContext}`,
      { ...this.metadata, ...metadata }
    );
  }

  /**
   * Get the context name
   */
  getContextName(): string {
    return this.contextName;
  }

  /**
   * Log an error with context
   */
  error(message: string, error?: unknown, metadata?: Record<string, unknown>): void {
    this.telemetryService.error(
      `[${this.contextName}] ${message}`,
      error,
      { ...this.metadata, ...metadata, className: this.contextName }
    );
  }

  /**
   * Log a warning with context
   */
  warn(message: string, metadata?: Record<string, unknown>): void {
    this.telemetryService.warn(
      `[${this.contextName}] ${message}`,
      { ...this.metadata, ...metadata, className: this.contextName }
    );
  }

  /**
   * Log an info message with context
   */
  info(message: string, metadata?: Record<string, unknown>): void {
    this.telemetryService.info(
      `[${this.contextName}] ${message}`,
      { ...this.metadata, ...metadata, className: this.contextName }
    );
  }

  /**
   * Log a debug message with context
   */
  debug(message: string, metadata?: Record<string, unknown>): void {
    this.telemetryService.debug(
      `[${this.contextName}] ${message}`,
      { ...this.metadata, ...metadata, className: this.contextName }
    );
  }

  /**
   * Track an exception with context
   */
  trackException(error: unknown, context?: Record<string, unknown>): void {
    this.telemetryService.trackException(error, {
      ...this.metadata,
      ...context,
      className: this.contextName,
    });
  }

  /**
   * Track a custom event with context
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    this.telemetryService.trackEvent(eventName, {
      ...this.metadata,
      ...properties,
      className: this.contextName,
    });
  }
}

